import React, { useState } from 'react';
import { useTranslation } from '../../contexts/TranslationContext';
import { Resource, UIComponent } from '../../types';

interface SimulationComponentProps {
  resource: Resource;
  component: UIComponent;
  onAction?: (action: string, data?: any) => void;
}

export const SimulationComponent: React.FC<SimulationComponentProps> = ({ resource, component, onAction }) => {
  const { t } = useTranslation();
  const [isRunning, setIsRunning] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [experimentData, setExperimentData] = useState<any>({});
  
  const components = resource.components || [];
  const isInteractive = resource.interactive || false;
  const preset = component.props?.preset || 'default';
  const componentInfo = resource.component_info || {};
  const simulationData = resource.simulation_data;

  const handleStart = () => {
    setIsRunning(true);
    setCurrentStep(0);
    const initialConditions = simulationData?.initial_conditions || { temperature: 25, humidity: 60 };
    setExperimentData(initialConditions);
    onAction?.('simulation_started', { components, preset });
  };

  const handleNext = () => {
    if (currentStep < components.length - 1) {
      const nextStep = currentStep + 1;
      setCurrentStep(nextStep);
      
      // Update experiment data based on simulation steps
      const stepData = simulationData?.steps?.find((s: any) => s.step === nextStep);
      if (stepData) {
        setExperimentData((prev: any) => ({ 
          ...prev, 
          ...stepData.conditions,
          status: stepData.status 
        }));
      }
      
      onAction?.('step_changed', { step: nextStep });
    }
  };

  const handleReset = () => {
    setIsRunning(false);
    setCurrentStep(0);
    setExperimentData({});
    onAction?.('simulation_reset', {});
  };

  return (
    <div className="bg-green-50 rounded-lg p-4">
      <div className="text-center mb-4">
        <h3 className="font-semibold text-green-900 mb-2">
          {simulationData?.title || t('simulation.virtualLabSimulation')}
        </h3>
        <p className="text-sm text-green-700">
          {simulationData?.description || t('simulation.interactiveScienceExperiment')}
        </p>
      </div>
      
      <div className="bg-white rounded-lg p-4 border-2 border-green-300">
        {/* Experiment Setup */}
        <div className="mb-4">
          <h4 className="font-medium text-green-800 mb-3">{t('simulation.experimentComponents')}:</h4>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {components.map((comp: string, index: number) => (
              <div
                key={comp}
                className={`p-0 rounded-lg border-2 transition-all ${
                  index === currentStep && isRunning
                    ? 'border-green-500 bg-green-50'
                    : 'border-gray-200 bg-gray-50'
                }`}
              >
                <div className="text-center">
                  <div className="text-2xl mb-1">
                    {componentInfo[comp]?.icon || "🔬"}
                  </div>
                  <div className="text-xs font-medium text-gray-700 leading-tight min-h-[40px] flex items-center justify-center px-1">
                    <div className="text-center w-full">
                      {componentInfo[comp]?.name?.split(' ').map((word: string, index: number) => (
                        <div key={index} className="break-words">
                          {word}
                        </div>
                      )) || comp.replace('_', ' ')}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Experiment Status */}
        {isRunning && (
          <div className="mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
            <div className="text-center">
              <p className="text-sm font-medium text-blue-800 mb-1">
                {experimentData.status || t('simulation.experimentInProgress')}
              </p>
              <div className="flex justify-center space-x-4 text-xs text-blue-600">
                <span>{t('simulation.temperature')}: {experimentData.temperature}°C</span>
                <span>{t('simulation.humidity')}: {experimentData.humidity}%</span>
                {experimentData.water_level && (
                  <span>{t('simulation.waterLevel')}: {experimentData.water_level}%</span>
                )}
              </div>
            </div>
          </div>
        )}
        
        {/* Controls */}
        <div className="flex justify-center space-x-2">
          {!isRunning ? (
            <button
              onClick={handleStart}
              disabled={!isInteractive}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 font-medium"
            >
              {t('simulation.startExperiment')}
            </button>
          ) : (
            <>
              <button
                onClick={handleNext}
                disabled={currentStep >= components.length - 1}
                className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 font-medium"
              >
                {t('simulation.nextStep')}
              </button>
              <button
                onClick={handleReset}
                className="px-3 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors font-medium"
              >
                {t('simulation.reset')}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
